import { ITimeoutService, IAugmentedJQuery } from 'angular';
import { EventService } from '@go-modules/services/event/event.service';
import type { GoEvent } from '@go-modules/services/event/event.service';
import { filter, map, mergeMap } from 'rxjs/operators';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { RubricService } from '@go-modules/services/rubric/rubric.service';
import { UserService } from '@go-modules/services/user/user.service';
import { firstValueFrom, of } from 'rxjs';
import { GoPrintService } from '@go-modules/services/go-print/go-print.service';
import { ActiveBrandService } from '@go-modules/services/active-brand';
import type {
	AutoGradedRubricGenerationState,
	RubricEvaluatorOption,
	RubricSession
} from '@go-modules/interfaces/rubrics/rubric';
import {
	AutoGradedRubricGenerationStatus,
	RubricEvaluatorOptionType
} from '@go-modules/interfaces/rubrics/rubric';
import type { User } from '@go-modules/interfaces/user';

export const RUBRIC_SESSION_VIEWS_STORAGE_KEY = 'rubricSessionViews';
const GO_REACT_AI_OPTION_ID = 'go-react-ai';

const AUTO_GRADED_RUBRIC_GENERATION_STATUS_ORDER = {
	[AutoGradedRubricGenerationStatus.NOT_APPLICABLE]: 0,
	[AutoGradedRubricGenerationStatus.NONE]: 1,
	[AutoGradedRubricGenerationStatus.QUEUED]: 2,
	[AutoGradedRubricGenerationStatus.PROCESSING]: 3,
	[AutoGradedRubricGenerationStatus.FAILED]: 4,
	[AutoGradedRubricGenerationStatus.COMPLETE]: 5
};

type AutoGradedRubricStateOption = RubricEvaluatorOption & {
	generationState: AutoGradedRubricGenerationState;
};

export interface Bindings {
	session: any;
	rubricList: any[];
	onRubricAdded;
	onRubricSaved;
	onRubricRemoved;
	userGroup: any;
	activity: any;
}

export class RubricFeedbackViewerController implements Bindings {

	public session;
	public rubricList;
	public onRubricAdded;
	public userGroup;
	public activity;
	public onRubricSaved;
	public onRubricRemoved;
	public evaluatorOptions: RubricEvaluatorOption[] = [];
	public selectedOption: RubricEvaluatorOption;
	public isRetryingAutoGradedRubric = false;
	public retryAutoGradedRubricError: string | null = null;
	public accessibilityAnnouncement = '';

	private areDescriptionsShowing: boolean;
	private selectedRubric: any;
	private rubricOptions: any;
	private rubricStorage: any;
	private templateCache: any;
	private eventSubscription: any;
	private eventNames: string[];
	private environmentVarsService;
	private aiUser: User | null = null;
	private autoGradedRubricGenerationState: AutoGradedRubricGenerationState | null = null;
	private contentController: any;
	private accessibilityAnnouncementTimeout: ng.IPromise<void> | null = null;

	/* @ngInject */
	constructor (
		private $timeout: ITimeoutService,
		private Group: any,
		private WebStorage: any,
		private RubricSessionModel: any,
		private RubricTemplateModel: any,
		private $scope: any,
		private confirmModal: any,
		private $element: IAugmentedJQuery,
		private ngxGoPrintService: GoPrintService,
		private $q: any,
		private eventService: EventService,
		private ngxRubricService: RubricService,
		private ngxUserService: UserService,
		private activeBrand: ActiveBrandService,
		private $translate: ng.translate.ITranslateService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public $onInit () {
		this.templateCache = {};
		this.rubricStorage = new this.WebStorage('rubric');
		this.areDescriptionsShowing = !this.rubricStorage.get('hideDesc');

		this.eventNames = [
			EVENT_NAMES.RUBRIC_SAVE_SESSION_DONE,
			EVENT_NAMES.RUBRIC_CHANGE,
			EVENT_NAMES.RUBRIC_READY,
			EVENT_NAMES.RUBRIC_FEEDBACK_VIEWER_AUTO_GRADED_STATE_UPDATED
		];
		this.eventSubscription = this.eventService.events
			.pipe(
				filter((ev: GoEvent) => this.eventNames.includes(ev.name)),
				mergeMap((ev: GoEvent) => {
					const selectedRubric = this.selectedRubric;

					if (
						ev.name === EVENT_NAMES.RUBRIC_READY &&
						selectedRubric != null &&
						this.rubricOptions?.mode === 'print' &&
						!selectedRubric.has_viewed
					) {
						return this.ngxRubricService
							.saveRubricSessionView(selectedRubric.id, selectedRubric.session_id)
							.pipe(map(() => ev));
					}
					return of(ev);
				})
			)
			.subscribe((ev: GoEvent) => {
				switch (ev.name) {
					case EVENT_NAMES.RUBRIC_SAVE_SESSION_DONE:
						this.$scope.saving = false;
						if (ev.data.succeeded) {
							this.$scope.dirty = false;

							if (ev.data.rubric.published_at) {
								this.session.setScore(ev.data.rubric.points);
							} else {
								this.session.setScore(null);
							}

							this.onRubricSaved({rubric: ev.data.rubric});
							this.eventService.broadcast(EVENT_NAMES.RUBRIC_FEEDBACK_VIEWER_RUBRIC_LIST_CHANGE,
								this.rubricList);
						}
						break;
					case EVENT_NAMES.RUBRIC_CHANGE:
						this.$scope.dirty = true;
						break;
					case EVENT_NAMES.RUBRIC_FEEDBACK_VIEWER_AUTO_GRADED_STATE_UPDATED:
						this.handleAutoGradedRubricGenerationStateUpdate(ev.data);
						break;
				}
			});

		this.setup();
		this.loadAutoGradedRubricOption();
	}

	public $onDestroy (): void {
		this.eventSubscription?.unsubscribe();
		this.clearAccessibilityAnnouncement();
	}

	public toggleDescriptions () {
		this.areDescriptionsShowing = !this.areDescriptionsShowing;
		this.rubricStorage.put('hideDesc', !this.areDescriptionsShowing);
		this.rubricOptions.hideDesc = !this.areDescriptionsShowing;
	}

	public saveSession () {
		this.$scope.saving = true;
		this.eventService.broadcast(EVENT_NAMES.RUBRIC_SAVE);
	}

	public togglePublish (rubric: any) {
		this.$scope.saving = true;

		this.eventService.broadcast(EVENT_NAMES.RUBRIC_SAVE, {callback: (succeeded: boolean) => {
			this.$scope.saving = false;
			if (!succeeded) {
				return;
			}

			this.$scope.publishing = true;
			rubric.publish()
				.then(() => {
					this.onRubricSaved({rubric});
					this.$scope.dirty = false;
				}).finally(() => {
					this.$scope.publishing = false;
				});
		}});
	}

	public shouldShowZeroState () {
		return this.evaluatorOptions.length === 0;
	}

	public shouldShowRubricContent (): boolean {
		return this.selectedRubric != null;
	}

	public shouldShowRubricActions (): boolean {
		return this.selectedRubric != null;
	}

	public shouldShowRubricFooter (): boolean {
		return this.selectedRubric != null;
	}

	public shouldShowAutoGradedRubricGenerationState (): boolean {
		return this.isSelectedAutoGradedRubricStateOnly();
	}

	public shouldShowAutoGradedEvaluatorBanner (): boolean {
		return this.isSelectedAutoGradedRubric() && this.userGroup.hasReviewerRole(true);
	}

	public shouldShowAutoGradedLearnerOverrideBanner (): boolean {
		return this.isSelectedAutoGradedRubric() && !this.userGroup.hasReviewerRole(true) &&
			this.selectedRubric.data.some((element) => this.isElementScoreOverridden(element));
	}

	public shouldShowProcessingState (): boolean {
		if (!this.isSelectedAutoGradedRubricStateOnly()) {
			return false;
		}

		return [
			AutoGradedRubricGenerationStatus.NONE,
			AutoGradedRubricGenerationStatus.QUEUED,
			AutoGradedRubricGenerationStatus.PROCESSING
		].includes(this.selectedAutoGradedRubricOption().generationState.status);
	}

	public shouldShowFailedState (): boolean {
		return this.isSelectedAutoGradedRubricStateOnly() &&
			this.selectedAutoGradedRubricOption().generationState.status === AutoGradedRubricGenerationStatus.FAILED;
	}

	public shouldShowRetryableFailedState (): boolean {
		return this.shouldShowFailedState() &&
			(this.selectedAutoGradedRubricOption().generationState.remaining_retries ?? 0) > 0;
	}

	public shouldShowExhaustedFailedState (): boolean {
		return this.shouldShowFailedState() &&
			this.selectedAutoGradedRubricOption().generationState.remaining_retries <= 0;
	}

	public shouldShowRetryAutoGradedRubric (): boolean {
		return this.shouldShowRetryableFailedState() &&
			this.selectedAutoGradedRubricOption().generationState.can_retry === true &&
			this.userGroup.hasInstructorRole(true);
	}

	public usedAutoGradedRubricRetries (): number {
		return Math.max(0, (this.selectedAutoGradedRubricOption().generationState.attempt_number ?? 1) - 1);
	}

	public totalAutoGradedRubricRetries (): number {
		return this.usedAutoGradedRubricRetries() +
			(this.selectedAutoGradedRubricOption().generationState.remaining_retries ?? 0);
	}

	public retryAutoGradedRubric (): void {
		if (!this.shouldShowRetryAutoGradedRubric() || this.isRetryingAutoGradedRubric) {
			return;
		}

		this.isRetryingAutoGradedRubric = true;
		this.retryAutoGradedRubricError = null;

		firstValueFrom(this.ngxRubricService.retryAutoGradedRubricGeneration(this.session.getId()))
			.then((generationState) => {
				this.isRetryingAutoGradedRubric = false;
				this.applyAutoGradedRubricGenerationState(generationState, false, true);
				this.focusAutoGradedRubricState();
			})
			.catch(() => {
				this.isRetryingAutoGradedRubric = false;
				this.retryAutoGradedRubricError = 'rubric-feedback-viewer_auto-graded-retry-error';
				this.refreshAutoGradedRubricGenerationStateAfterRetryError();
			});
	}

	public failedStateGraphicUrl (): string {
		return this.activeBrand.getImage('floating-astronaut.svg');
	}

	public shouldShowRubricScore (rubric: any, userGroup: any) {
		return userGroup.hasReviewerRole(true) || this.Group.model(rubric).hasReviewerRole(true);
	}

	public printRubric (rubric: any) {
		const mode = this.rubricOptions.mode;

		// We want rubric in print mode when we send the element
		// to print service, flip it back to what it was right after
		this.rubricOptions.mode = 'print';
		this.$timeout(() => {
			this.ngxGoPrintService.print(
				this.$element[0].querySelector('.rubric-feedback-viewer-content > .rubric'),
				`Rubric: ${rubric.user.first_name} ${rubric.user.last_name}`
			);
			this.rubricOptions.mode = mode;
		});
	}

	public deleteRubric (rubric: any) {
		this.confirmModal.open({
			modalData: {
				title: 'rubric-feedback-viewer_remove-rubric',
				message: 'rubric-feedback-viewer_confirm-remove-rubric',
				yes: 'common_remove',
				no: 'common_cancel'
			}
		}).result.then(() => {
			rubric.delete()
				.then(() => {
					this.removeRubric(rubric);
					this.setup();
				});
		});
	}

	public shouldShowDelete (): boolean {
		if (this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)) {
			return false;
		}

		return this.selectedRubric.isSaved() &&
			(this.selectedRubric.isOwner(this.userGroup) || this.userGroup.hasInstructorRole(true));
	}

	public shouldShowPublishToggle (): boolean {
		if (this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)) {
			return false;
		}

		return (this.selectedRubric?.id != null || this.$scope.dirty) && this.selectedRubric?.isOwner(this.userGroup);
	}

	public mayEditRubric (rubric) {
		if (this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)) {
			return false;
		}

		return rubric.isOwner(this.userGroup) || rubric.can_override_score === true;
	}

	public setRubric (rubric: any) {
		this.rubricOptions = null;

		this.$timeout(() => {
			this.rubricOptions = {
				session: rubric,
				schemaId: rubric.template_id,
				mode: this.mayEditRubric(rubric) ? 'live' : 'print',
				postData: {session_id: this.session.getId()},
				hideDesc: !this.areDescriptionsShowing,
				userGroup: this.userGroup,
				showAutoGradedJustifications: this.isAutoGradedRubric(rubric),
				isInstructor: this.userGroup.hasInstructorRole(true),
				mayEditAutoGradedComment: () => this.mayEditRubric(rubric)
			};
		});

		// Set total on this rubric
		this.getRubricTemplate(rubric.template_id)
			.then((template) => {
				rubric.total = template.total;
			});
	}

	public onRubricChange (rubric) {
		this.applySelectedOption(rubric);

		if (this.isSelectedAutoGradedRubricStateOnly()) {
			this.$scope.$evalAsync(() => {
				this.announceAutoGradedRubricGenerationState(this.autoGradedRubricGenerationState);
			});
		} else {
			this.clearAccessibilityAnnouncement();
			this.$scope.$evalAsync();
		}
	}

	private setup () {
		// This will create and add a rubric if the current user
		// can create one for this activity
		this.addMyRubric(this.rubricList, this.userGroup);

		this.rubricList = this.sortRubrics().map((rubric) => {

			// Mark self-critique rubrics for correct icon
			rubric.isSelfCritique = this.session.isOwner({
				user_id: rubric.created_by
			});

			rubric.hideScore = !this.shouldShowRubricScore(rubric, this.userGroup);

			return rubric;
		});

		this.buildEvaluatorOptions();

		if (this.evaluatorOptions.length === 0) {
			return;
		}

		this.selectInitialOption();
	}

	private findInitialRubric (rubricList: any[], user: any) {
		const rubric = rubricList.filter((_rubric) => {
			return _rubric.isOwner(user);
		});

		return rubric[0] || rubricList[0];
	}

	private selectInitialOption (): void {
		const selectedOptionId = this.selectedOption?.id;
		const humanRubrics = this.rubricList.filter((rubric) => !this.isAutoGradedRubric(rubric));
		const initialRubric = this.findInitialRubric(humanRubrics, this.userGroup);
		const initialOptionId = initialRubric == null ? null : this.optionIdForRubric(initialRubric);
		const selectedOption = this.findEvaluatorOption(selectedOptionId) ??
			this.findEvaluatorOption(initialOptionId) ??
			this.evaluatorOptions[0];

		this.applySelectedOption(selectedOption);
	}

	private buildEvaluatorOptions (): void {
		const humanOptions = this.rubricList
			.filter((rubric) => !this.isAutoGradedRubric(rubric))
			.map((rubric) => ({
				id: this.optionIdForRubric(rubric),
				rubric,
				type: RubricEvaluatorOptionType.HUMAN,
				user: rubric.user
			}));

		const aiOption = this.buildAutoGradedRubricOption();
		this.evaluatorOptions = aiOption == null ? humanOptions : [aiOption, ...humanOptions];
	}

	private buildAutoGradedRubricOption (): RubricEvaluatorOption | null {
		if (this.aiUser == null || this.autoGradedRubricGenerationState == null) {
			return null;
		}

		if (this.autoGradedRubricGenerationState.status === AutoGradedRubricGenerationStatus.NOT_APPLICABLE) {
			return null;
		}

		return {
			generationState: this.autoGradedRubricGenerationState,
			id: GO_REACT_AI_OPTION_ID,
			rubric: this.autoGradedRubricGenerationState.status === AutoGradedRubricGenerationStatus.COMPLETE
				? this.findAutoGradedRubric()
				: null,
			type: RubricEvaluatorOptionType.GO_REACT_AI,
			user: this.aiUser
		};
	}

	private findEvaluatorOption (optionId: string | null): RubricEvaluatorOption | undefined {
		if (optionId == null) {
			return undefined;
		}

		return this.evaluatorOptions.find((option) => option.id === optionId);
	}

	private applySelectedOption (option: RubricEvaluatorOption): void {
		this.selectedOption = option;
		this.selectedRubric = option.rubric;
		this.rubricOptions = null;

		if (this.selectedRubric != null) {
			this.setRubric(this.selectedRubric);
		}

		this.eventService.broadcast(
			EVENT_NAMES.RUBRIC_FEEDBACK_VIEWER_AUTO_GRADED_STATE_ONLY_SELECTED,
			{selected: this.isSelectedAutoGradedRubricStateOnly()}
		);
	}

	private isSelectedAutoGradedRubricStateOnly (): boolean {
		return this.selectedOption?.type === RubricEvaluatorOptionType.GO_REACT_AI &&
			this.selectedOption.rubric == null;
	}

	private selectedAutoGradedRubricOption (): AutoGradedRubricStateOption {
		return this.selectedOption as AutoGradedRubricStateOption;
	}

	private isAutoGradedRubric (rubric: RubricSession): boolean {
		return rubric.is_ai_generated === true;
	}

	private isSelectedAutoGradedRubric (): boolean {
		return this.selectedOption?.type === RubricEvaluatorOptionType.GO_REACT_AI && this.selectedRubric != null;
	}

	private isElementScoreOverridden (element: any): boolean {
		if (element.original_score == null) {
			return false;
		}

		if (element.option != null || element.original_score.rubric_element_option_id != null) {
			return element.option !== element.original_score.rubric_element_option_id;
		}

		const currentValue = element.points ?? element.value;
		return `${currentValue ?? ''}` !== `${element.original_score.value ?? ''}`;
	}

	private findAutoGradedRubric (): RubricSession | null {
		const rubricSessionId = this.autoGradedRubricGenerationState?.rubric_session_id;

		if (rubricSessionId != null) {
			const rubric = this.rubricList.find((candidate) => candidate.id === rubricSessionId);
			if (rubric != null) {
				return rubric;
			}
		}

		return this.rubricList.find((rubric) => this.isAutoGradedRubric(rubric)) ?? null;
	}

	private optionIdForRubric (rubric: RubricSession): string {
		return `rubric-session-${rubric.id ?? rubric.created_by}`;
	}

	private loadAutoGradedRubricOption (): void {
		firstValueFrom(this.ngxRubricService.getAutoGradedRubricGenerationState(this.session.getId()))
			.then((generationState) => {
				if (generationState.status === AutoGradedRubricGenerationStatus.NOT_APPLICABLE) {
					this.applyAutoGradedRubricGenerationState(generationState, false, true);
					return null;
				}

				return firstValueFrom(this.ngxUserService.getAiUser())
					.then((aiUser) => {
						this.aiUser = aiUser;
						this.applyAutoGradedRubricGenerationState(generationState, false, true);
					});
			})
			.catch(() => {});
	}

	private applyAutoGradedRubricGenerationState (
		generationState: AutoGradedRubricGenerationState,
		force = false,
		announce = false,
		clearRetryError = true
	): void {
		if (!force && !this.isNewerAutoGradedRubricGenerationState(generationState)) {
			return;
		}

		this.autoGradedRubricGenerationState = generationState;
		if (clearRetryError) {
			this.retryAutoGradedRubricError = null;
		}

		if (announce && this.isSelectedAutoGradedRubricStateOnly()) {
			this.announceAutoGradedRubricGenerationState(generationState);
		}

		if (generationState.status !== AutoGradedRubricGenerationStatus.NOT_APPLICABLE && this.aiUser == null) {
			firstValueFrom(this.ngxUserService.getAiUser())
				.then((aiUser) => {
					this.aiUser = aiUser;
					this.renderAutoGradedRubricGenerationState(generationState);
				});
			return;
		}

		this.renderAutoGradedRubricGenerationState(generationState);
	}

	private renderAutoGradedRubricGenerationState (generationState: AutoGradedRubricGenerationState): void {
		if (generationState.status === AutoGradedRubricGenerationStatus.COMPLETE &&
			generationState.rubric_session_id != null) {
			this.refreshRubricsAfterAutoGradedRubricCompletion();
			return;
		}

		this.setup();
		this.$scope.$evalAsync();
	}

	private refreshRubricsAfterAutoGradedRubricCompletion (): void {
		this.contentController.getFeedback()
			.then((feedback) => {
				this.rubricList = feedback.rubric;
				this.setup();
				this.$scope.$evalAsync();
			});
	}

	private refreshAutoGradedRubricGenerationStateAfterRetryError (): void {
		firstValueFrom(this.ngxRubricService.getAutoGradedRubricGenerationState(this.session.getId()))
			.then((generationState) => {
				this.applyAutoGradedRubricGenerationState(generationState, true, false, false);
				this.focusAutoGradedRubricRetryError();
			});
	}

	private handleAutoGradedRubricGenerationStateUpdate (
		generationState: AutoGradedRubricGenerationState
	): void {
		if (!this.userGroup.hasReviewerRole(true)) {
			return;
		}

		this.applyAutoGradedRubricGenerationState(generationState, false, true);

		if (
			generationState.status === AutoGradedRubricGenerationStatus.FAILED &&
			this.userGroup.hasInstructorRole(true)
		) {
			firstValueFrom(this.ngxRubricService.getAutoGradedRubricGenerationState(this.session.getId()))
				.then((authenticatedState) => {
					this.applyAutoGradedRubricGenerationState(authenticatedState);
				})
				.catch(() => {});
		}
	}

	private announceAutoGradedRubricGenerationState (generationState: AutoGradedRubricGenerationState): void {
		if (generationState.status === AutoGradedRubricGenerationStatus.COMPLETE) {
			this.announceAccessibilityMessage('rubric-feedback-viewer_auto-graded-complete-announcement');
		} else if (generationState.status === AutoGradedRubricGenerationStatus.FAILED) {
			const messageKey = generationState.remaining_retries === 0
				? 'rubric-feedback-viewer_auto-graded-exhausted-title'
				: 'rubric-feedback-viewer_auto-graded-failed-title';
			this.announceAccessibilityMessage(messageKey);
		} else if ([
			AutoGradedRubricGenerationStatus.NONE,
			AutoGradedRubricGenerationStatus.QUEUED,
			AutoGradedRubricGenerationStatus.PROCESSING
		].includes(generationState.status)) {
			this.announceAccessibilityMessage('rubric-feedback-viewer_auto-graded-processing-title');
		}
	}

	private announceAccessibilityMessage (messageKey: string): void {
		this.clearAccessibilityAnnouncement();

		const message = this.$translate.instant(messageKey);
		this.accessibilityAnnouncementTimeout = this.$timeout(() => {
			this.accessibilityAnnouncement = message;
			this.accessibilityAnnouncementTimeout = null;
		}, 100);
	}

	private clearAccessibilityAnnouncement (): void {
		if (this.accessibilityAnnouncementTimeout != null) {
			this.$timeout.cancel(this.accessibilityAnnouncementTimeout);
			this.accessibilityAnnouncementTimeout = null;
		}

		this.accessibilityAnnouncement = '';
	}

	private focusAutoGradedRubricState (): void {
		this.$timeout(() => {
			this.$element[0]
				.querySelector<HTMLElement>('.rubric-feedback-viewer__auto-graded-state-title')
				?.focus();
		});
	}

	private focusAutoGradedRubricRetryError (): void {
		this.$timeout(() => {
			this.$element[0]
				.querySelector<HTMLElement>('.rubric-feedback-viewer__auto-graded-retry-error')
				?.focus();
		});
	}

	private isNewerAutoGradedRubricGenerationState (generationState: AutoGradedRubricGenerationState): boolean {
		if (this.autoGradedRubricGenerationState == null) {
			return true;
		}

		const currentAttempt = this.autoGradedRubricGenerationState.attempt_number ?? 0;
		const incomingAttempt = generationState.attempt_number ?? 0;

		if (incomingAttempt > currentAttempt) {
			return true;
		}

		if (incomingAttempt < currentAttempt) {
			return false;
		}

		return AUTO_GRADED_RUBRIC_GENERATION_STATUS_ORDER[generationState.status] >=
			AUTO_GRADED_RUBRIC_GENERATION_STATUS_ORDER[this.autoGradedRubricGenerationState.status];
	}

	private sortRubrics () {
		return this.rubricList.sort((a, b) => {
			const aGroup = this.Group.model(a), bGroup = this.Group.model(b);

			if (aGroup.hasReviewerRole(true) && bGroup.hasPresenterRole()) {
				return -1;
			}

			if (bGroup.hasReviewerRole(true) && aGroup.hasPresenterRole()) {
				return 1;
			}

			// Sort by last name after putting reviewer and above first
			return a.user.last_name.toLowerCase() > b.user.last_name.toLowerCase() ? 1 : -1;
		});
	}

	private addMyRubric (rubricList: any[], userGroup: any) {
		const existingRubric = rubricList.filter((rubric) => {
			return rubric.isOwner(userGroup);
		});

		if (existingRubric.length === 0 && this.shouldAddRubric(userGroup, this.activity)) {
			const rubric = this.RubricSessionModel.newInstance();
			rubric.session_id = this.session.getId();
			rubric.setOwner(userGroup);
			rubric.isSelfCritique = this.session.isOwner(userGroup);
			rubric.role = userGroup.role;
			rubric.hideScore = !this.shouldShowRubricScore(rubric, userGroup);

			// Need to set right total based on which template to use
			const templateId = userGroup.hasReviewerRole(true)
				? this.activity.getRubricTemplateId() : this.activity.getPeerRubricTemplateId();
			rubric.setTemplate(templateId);

			this.getRubricTemplate(templateId)
				.then((template) => {
					rubric.total = template.total;
				});

			rubricList.push(rubric);

			this.onRubricAdded({rubric});
			this.eventService.broadcast(EVENT_NAMES.RUBRIC_FEEDBACK_VIEWER_RUBRIC_LIST_CHANGE, this.rubricList);
		}
	}

	private shouldAddRubric (userGroup: any, activity: any) {
		return activity.getRubricTemplateId() && userGroup.hasReviewerRole(true)
			|| (activity.getPeerRubricTemplateId() && !userGroup.hasReviewerRole(true));
	}

	private getRubricTemplate (templateId: string) {
		return this.$q((resolve, _reject) => {
			let template = this.templateCache[templateId];
			if (!template) {
				template = this.RubricTemplateModel.get({id: templateId});
				this.templateCache[templateId] = template;
			}
			template.$promise.then((_template) => {
				resolve(_template);
			});
		});
	}

	/**
	 * Remove a rubric from the list
	 */
	private removeRubric (rubric: any): void {
		const index: number = this.rubricList.indexOf(rubric);
		if (index !== -1) {
			this.rubricList.splice(index, 1);
			this.onRubricRemoved({rubric});
			this.eventService.broadcast(EVENT_NAMES.RUBRIC_FEEDBACK_VIEWER_RUBRIC_LIST_CHANGE, this.rubricList);
		}
	}
}
